import React, { useEffect, useMemo, useState } from "react"
import { Button, Card, CardBody, Col, Row } from "reactstrap"
import Select, { components as RSComponents } from "react-select"
import { FaFilter, FaUser, FaBoxOpen } from "react-icons/fa"
import { customStyles } from "utils/SelectStyles"
import { DatePicker } from "antd"
import { months } from "utils/defaultpositions"
import secureLocalStorage from "react-secure-storage"
import dayjs from "dayjs"
import { FetchPostData } from "utils/Common_api_functions"
import Loader from "components/Loader/loader"

const { RangePicker } = DatePicker

const currentYear = new Date().getFullYear()
const years = Array.from({ length: currentYear - 2020 }, (_, i) => ({
  label: `${2021 + i}`,
  value: 2021 + i,
})).reverse()

const fmtGBP = v => {
  const n = Number(v) || 0
  const abs = Math.abs(n)
  return (
    (n < 0 ? "-£" : "£") +
    abs.toLocaleString("en-GB", { maximumFractionDigits: 2 })
  )
}

// ── Sort Order cycling button ────────────────────────────────────────────────
const SORT_STATES = [
  { key: "default", icon: "⇅", label: "Sort", color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
  { key: "ascending", icon: "↑", label: "Low → High", color: "#2563eb", bg: "#eff6ff", border: "#bfdbfe" },
  { key: "descending", icon: "↓", label: "High → Low", color: "#0f766e", bg: "#f0fdfa", border: "#99f6e4" },
]

const SortButton = ({ sortOrder, onToggle }) => {
  const current = SORT_STATES.find(s => s.key === sortOrder) || SORT_STATES[0]
  return (
    <button
      onClick={onToggle}
      title={`Sort Order: ${current.label} — click to change`}
      style={{
        display: "flex", alignItems: "center", gap: "8px",
        background: current.bg, border: `1.5px solid ${current.border}`,
        borderRadius: "10px", padding: "5px 12px",
        cursor: "pointer", outline: "none",
        transition: "all 0.18s ease", whiteSpace: "nowrap",
        userSelect: "none", minWidth: "115px",
      }}
    >
      <span style={{ fontSize: "15px", lineHeight: 1, color: current.color, fontWeight: 700, minWidth: "16px", textAlign: "center" }}>
        {current.icon}
      </span>
      <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
        <span style={{ fontSize: "8px", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", lineHeight: 1.1 }}>
          Sort Order
        </span>
        <span style={{ fontSize: "11px", fontWeight: 700, color: current.color, lineHeight: 1.3 }}>
          {current.label}
        </span>
      </span>
    </button>
  )
}

const SIGN_STYLE = {
  positive: { label: "+", activeColor: "#15803d", activeBg: "#f0fdf4", border: "#86efac", title: "Show positive only" },
  negative: { label: "−", activeColor: "#be123c", activeBg: "#fff1f2", border: "#fca5a5", title: "Show negative only" },
}

const SignFilterButton = ({ sign, value, onToggle }) => {
  const s = SIGN_STYLE[sign]
  const active = value === sign
  return (
    <button
      onClick={() => onToggle(sign)}
      title={s.title}
      style={{
        width: "20px",
        height: "20px",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "12px",
        fontWeight: 800,
        lineHeight: 1,
        cursor: "pointer",
        borderRadius: "4px",
        flexShrink: 0,
        border: `1px solid ${active ? s.border : "#e2e8f0"}`,
        background: active ? s.activeBg : "#fff",
        color: active ? s.activeColor : "#94a3b8",
      }}
    >
      {s.label}
    </button>
  )
}

const SELECT_ALL_VALUE = "__select_all__"

const CheckboxOption = ({ innerProps, label, isSelected, isFocused, data, selectProps }) => {
  const isSelectAll = data?.value === SELECT_ALL_VALUE
  const allOpts = isSelectAll
    ? (selectProps?.options || []).filter(o => o.value !== SELECT_ALL_VALUE)
    : []
  const selectedCount = isSelectAll ? (selectProps?.value || []).length : 0
  const allSelected = isSelectAll && allOpts.length > 0 && selectedCount === allOpts.length
  const checked = isSelectAll ? allSelected : isSelected

  return (
    <div
      {...innerProps}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "8px",
        padding: "8px 10px",
        cursor: "pointer",
        background: isFocused ? "#f1f5f9" : isSelectAll ? "#f8faff" : "#fff",
        borderBottom: isSelectAll ? "1px solid #e2e8f0" : "none",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => { }}
        style={{ accentColor: "#3b82f6", width: 14, height: 14, flexShrink: 0, cursor: "pointer" }}
      />
      <span
        style={{
          fontSize: "13px",
          color: isSelectAll ? "#1d4ed8" : "#1e293b",
          fontWeight: checked || isSelectAll ? 700 : 400,
        }}
      >
        {label}
      </span>
    </div>
  )
}

const CollapsibleValueContainer = ({ children, ...props }) => {
  const selected = props.getValue()
  const realOpts = (props.selectProps?.options || []).filter(o => o.value !== SELECT_ALL_VALUE)
  const allSelected = realOpts.length > 0 && selected.length === realOpts.length
  if (allSelected) {
    const input = React.Children.toArray(children).slice(-1)
    return (
      <RSComponents.ValueContainer {...props}>
        <span style={{ color: "#1d4ed8", fontWeight: 600, fontSize: "13px", padding: "2px 0" }}>
          All ({realOpts.length}) selected
        </span>
        {input}
      </RSComponents.ValueContainer>
    )
  }
  return <RSComponents.ValueContainer {...props}>{children}</RSComponents.ValueContainer>
}

const NetGrossMarginReport = () => {
  const [loading, setLoading] = useState(false)
  const [reportData, setReportData] = useState({})

  const [selectedCustomers, setSelectedCustomers] = useState([])
  const [customerSort, setCustomerSort] = useState("asc")
  const [customerMenuOpen, setCustomerMenuOpen] = useState(false)

  const [selectedProducts, setSelectedProducts] = useState([])
  const [productSort, setProductSort] = useState("asc")
  const [productMenuOpen, setProductMenuOpen] = useState(false)

  const [dateRange, setDateRange] = useState([])
  const [selectedMonths, setSelectedMonths] = useState([])
  const [selectedYears, setSelectedYears] = useState([])
  const [maxDate, setMaxDate] = useState(null)
  const [sortOrder, setSortOrder] = useState("default")

  const handleSortToggle = () => {
    setSortOrder(prev =>
      prev === "default" ? "ascending" : prev === "ascending" ? "descending" : "default"
    )
  }

  useEffect(() => {
    const Years = secureLocalStorage?.getItem("Years")
    const Months = secureLocalStorage?.getItem("Months")
    const DateRange = secureLocalStorage?.getItem("DateRange")
    const FinalRangeRaw = secureLocalStorage?.getItem("FINALRANGE")

    setSelectedYears(Years || [])
    setSelectedMonths(Months || [])
    const activeDateRange = DateRange
      ? DateRange.map(d => dayjs(d))
      : FinalRangeRaw
        ? FinalRangeRaw.map(d => dayjs(d))
        : []
    setDateRange(activeDateRange)
    setMaxDate(FinalRangeRaw?.[1] ? dayjs(FinalRangeRaw[1]) : null)

    const fetchInitialData = async () => {
      const res = await FetchPostData(setLoading, "sheet/customer/report", {
        startDate: activeDateRange?.[0]?.isValid()
          ? activeDateRange[0].format("YYYY-MM-DD")
          : undefined,
        endDate: activeDateRange?.[1]?.isValid()
          ? activeDateRange[1].format("YYYY-MM-DD")
          : undefined,
        month: Months?.map(i => i?.value)?.join(",") || undefined,
        year: Years?.map(i => i?.value)?.join(",") || undefined,
      })
      if (res?.data?.success) setReportData(res.data?.data)
      else setReportData({})
    }

    fetchInitialData()
  }, [])

  const handleFilter = async () => {
    const res = await FetchPostData(setLoading, "sheet/customer/report", {
      startDate: dateRange?.[0]?.isValid() ? dateRange[0].format("YYYY-MM-DD") : undefined,
      endDate: dateRange?.[1]?.isValid() ? dateRange[1].format("YYYY-MM-DD") : undefined,
      month: selectedMonths?.map(i => i?.value)?.join(","),
      year: selectedYears?.map(i => i?.value)?.join(","),
    })
    if (res?.data?.success) setReportData(res.data?.data)
    else setReportData({})
  }

  const rawRecords = useMemo(() => {
    if (!reportData || typeof reportData !== "object") return []
    return Array.isArray(reportData) ? reportData : Object.values(reportData)
  }, [reportData])

  const customerOptions = useMemo(() => {
    const seen = new Set()
    const opts = []
    rawRecords.forEach(r => {
      if (!seen.has(r.customerCode)) {
        seen.add(r.customerCode)
        opts.push({ label: r.customerName, value: r.customerCode })
      }
    })
    return opts
  }, [rawRecords])

  const sortedCustomerOptions = useMemo(
    () =>
      [...customerOptions].sort((a, b) =>
        customerSort === "asc" ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label)
      ),
    [customerOptions, customerSort]
  )

  const customerOptionsWithAll = useMemo(
    () =>
      sortedCustomerOptions.length > 0
        ? [{ label: "Select All", value: SELECT_ALL_VALUE }, ...sortedCustomerOptions]
        : sortedCustomerOptions,
    [sortedCustomerOptions]
  )

  const handleCustomerChange = selected => {
    if (!selected) return setSelectedCustomers([])
    if (selected.some(o => o.value === SELECT_ALL_VALUE)) {
      const allPicked = selectedCustomers.length === sortedCustomerOptions.length
      setSelectedCustomers(allPicked ? [] : sortedCustomerOptions)
      setCustomerMenuOpen(false)
    } else {
      setSelectedCustomers(selected)
    }
  }

  const productOptions = useMemo(() => {
    const seen = new Set()
    const opts = []
    rawRecords.forEach(r => {
      if (!seen.has(r.productCode)) {
        seen.add(r.productCode)
        opts.push({ label: r.productName, value: r.productCode })
      }
    })
    return opts
  }, [rawRecords])

  const sortedProductOptions = useMemo(
    () =>
      [...productOptions].sort((a, b) =>
        productSort === "asc" ? a.label.localeCompare(b.label) : b.label.localeCompare(a.label)
      ),
    [productOptions, productSort]
  )

  const productOptionsWithAll = useMemo(
    () =>
      sortedProductOptions.length > 0
        ? [{ label: "Select All", value: SELECT_ALL_VALUE }, ...sortedProductOptions]
        : sortedProductOptions,
    [sortedProductOptions]
  )

  const handleProductChange = selected => {
    if (!selected) return setSelectedProducts([])
    if (selected.some(o => o.value === SELECT_ALL_VALUE)) {
      const allPicked = selectedProducts.length === sortedProductOptions.length
      setSelectedProducts(allPicked ? [] : sortedProductOptions)
      setProductMenuOpen(false)
    } else {
      setSelectedProducts(selected)
    }
  }

  // Rows filtered by selected customers/products, aggregated per (customer, product) pair
  const tableRows = useMemo(() => {
    const customerSet = selectedCustomers.length
      ? new Set(selectedCustomers.map(c => c.value))
      : null
    const productSet = selectedProducts.length
      ? new Set(selectedProducts.map(p => p.value))
      : null

    const map = {}
    rawRecords.forEach(r => {
      if (customerSet && !customerSet.has(r.customerCode)) return
      if (productSet && !productSet.has(r.productCode)) return

      const key = `${r.customerCode}__${r.productCode}`
      if (!map[key]) {
        map[key] = {
          customerName: r.customerName,
          productName: r.productName,
          salesValue: 0,
          netMarginContribution: 0,
          grossMarginContribution: 0,
          grossPctNumerator: 0,
        }
      }
      const sales = Number(r.weeklySalesValue || r.salesValue || 0)
      const grossValue = Number(r.grossMarginValue || 0)
      map[key].salesValue += sales
      map[key].netMarginContribution += Number(r.netMarginContribution || 0)
      map[key].grossMarginContribution += grossValue
      // Gross % comes straight from the API's grossMarginPercent (a fraction,
      // e.g. 0.15 = 15%); weighted by sales when several weeks are combined.
      map[key].grossPctNumerator += Number(r.grossMarginPercent || 0) * sales
    })

    return Object.values(map).map(row => ({
      ...row,
      netMarginPercent:
        row.salesValue !== 0 ? (row.netMarginContribution / row.salesValue) * 100 : 0,
      grossMarginPercent:
        row.salesValue !== 0 ? (row.grossPctNumerator / row.salesValue) * 100 : 0,
    }))
  }, [rawRecords, selectedCustomers, selectedProducts])

  const sortedTableRows = useMemo(() => {
    if (sortOrder === "default") return tableRows
    return [...tableRows].sort((a, b) =>
      sortOrder === "ascending"
        ? a.netMarginContribution - b.netMarginContribution
        : b.netMarginContribution - a.netMarginContribution
    )
  }, [tableRows, sortOrder])

  // Sign filters, one per column: "all" | "positive" | "negative"
  const [grossPercentFilter, setGrossPercentFilter] = useState("all")
  const [grossSignFilter, setGrossSignFilter] = useState("all")
  const [netPercentFilter, setNetPercentFilter] = useState("all")
  const [netSignFilter, setNetSignFilter] = useState("all")
  // false (default) = hide rows where all 4 margin columns are 0
  const [showZeroRows, setShowZeroRows] = useState(false)

  const toggleSignFilter = (current, setFn, sign) => {
    setFn(current === sign ? "all" : sign)
  }

  const filteredTableRows = useMemo(() => {
    return sortedTableRows.filter(row => {
      if (
        !showZeroRows &&
        row.grossMarginPercent === 0 &&
        row.grossMarginContribution === 0 &&
        row.netMarginPercent === 0 &&
        row.netMarginContribution === 0
      )
        return false
      if (grossPercentFilter === "positive" && row.grossMarginPercent < 0) return false
      if (grossPercentFilter === "negative" && row.grossMarginPercent >= 0) return false
      if (grossSignFilter === "positive" && row.grossMarginContribution < 0) return false
      if (grossSignFilter === "negative" && row.grossMarginContribution >= 0) return false
      if (netPercentFilter === "positive" && row.netMarginPercent < 0) return false
      if (netPercentFilter === "negative" && row.netMarginPercent >= 0) return false
      if (netSignFilter === "positive" && row.netMarginContribution < 0) return false
      if (netSignFilter === "negative" && row.netMarginContribution >= 0) return false
      return true
    })
  }, [sortedTableRows, grossPercentFilter, grossSignFilter, netPercentFilter, netSignFilter, showZeroRows])

  const grossTotal = filteredTableRows.reduce((s, r) => s + Number(r.grossMarginContribution), 0)
  const netTotal = filteredTableRows.reduce((s, r) => s + Number(r.netMarginContribution), 0)

  const productFirst = selectedProducts.length > 0 && selectedCustomers.length === 0

  return (
    <div className="page-content">
      {loading && <Loader />}
      <div className="container-fluid">
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            padding: "18px 22px",
            marginBottom: "18px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            border: "1px solid #e2e8f0",
          }}
        >
          <div className="d-flex justify-content-between align-items-center flex-wrap gap-3">
            <div>
              <h4 style={{ margin: 0, fontSize: "18px", fontWeight: 700, color: "#000" }}>
                Net & Gross Margin Report
              </h4>
              <p style={{ margin: 0, fontSize: "12px", color: "#666" }}>
                Customer & product level net and gross margin
              </p>
            </div>

            <div className="d-flex align-items-center gap-2 flex-wrap">
              <RangePicker
                value={dateRange}
                placeholder={["Start (Sat)", "End (Fri)"]}
                onChange={vals => setDateRange(vals || [])}
                format="DD-MM-YYYY"
                style={{ borderRadius: "8px", height: "38px" }}
                disabledDate={current =>
                  maxDate ? current && current > maxDate.endOf("day") : false
                }
              />
              <Select
                placeholder="Month"
                options={months}
                styles={customStyles}
                isMulti
                value={selectedMonths}
                onChange={v => setSelectedMonths(v || [])}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Select
                placeholder="Year"
                options={years}
                styles={customStyles}
                isMulti
                value={selectedYears}
                onChange={v => setSelectedYears(v || [])}
                menuPortalTarget={document.body}
                menuPosition="fixed"
              />
              <Button onClick={handleFilter} className="btn-blue rounded-2 border-transparent">
                <FaFilter size={12} />
              </Button>
            </div>
          </div>
        </div>

        {/* ── Customer + Product Filters ─────────────────────────────────────── */}
        <Card className="mb-4 shadow-sm" style={{ borderRadius: "16px", border: "1px solid #dbeafe" }}>
          <CardBody style={{ background: "#f8faff", borderRadius: "16px" }}>
            <Row className="g-3">
              <Col md={6}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <FaUser size={12} color="#3b82f6" />
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "#1e40af" }}>
                    Customer(s)
                  </span>
                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>→ filters products</span>
                  <button
                    onClick={() => setCustomerSort(s => (s === "asc" ? "desc" : "asc"))}
                    style={{
                      marginLeft: "auto",
                      background: "#eff6ff",
                      border: "1px solid #bfdbfe",
                      borderRadius: "6px",
                      padding: "2px 9px",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#1d4ed8",
                      cursor: "pointer",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {customerSort === "asc" ? "A → Z" : "Z → A"}
                  </button>
                </div>
                <Select
                  isMulti
                  options={customerOptionsWithAll}
                  value={selectedCustomers}
                  onChange={handleCustomerChange}
                  styles={customStyles}
                  placeholder="Select customer(s)…"
                  closeMenuOnSelect={false}
                  menuIsOpen={customerMenuOpen}
                  onMenuOpen={() => setCustomerMenuOpen(true)}
                  onMenuClose={() => setCustomerMenuOpen(false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{ Option: CheckboxOption, ValueContainer: CollapsibleValueContainer }}
                  hideSelectedOptions={false}
                />
              </Col>

              <Col md={6}>
                <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "8px" }}>
                  <FaBoxOpen size={12} color="#8b5cf6" />
                  <span style={{ fontSize: "13px", fontWeight: 700, color: "#6d28d9" }}>
                    Product(s)
                  </span>
                  <span style={{ fontSize: "11px", color: "#94a3b8" }}>→ filters customers</span>
                  <button
                    onClick={() => setProductSort(s => (s === "asc" ? "desc" : "asc"))}
                    style={{
                      marginLeft: "auto",
                      background: "#faf5ff",
                      border: "1px solid #e9d5ff",
                      borderRadius: "6px",
                      padding: "2px 9px",
                      fontSize: "11px",
                      fontWeight: 700,
                      color: "#7c3aed",
                      cursor: "pointer",
                      letterSpacing: "0.3px",
                    }}
                  >
                    {productSort === "asc" ? "A → Z" : "Z → A"}
                  </button>
                </div>
                <Select
                  isMulti
                  options={productOptionsWithAll}
                  value={selectedProducts}
                  onChange={handleProductChange}
                  styles={customStyles}
                  placeholder="Select product(s)…"
                  closeMenuOnSelect={false}
                  menuIsOpen={productMenuOpen}
                  onMenuOpen={() => setProductMenuOpen(true)}
                  onMenuClose={() => setProductMenuOpen(false)}
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  components={{ Option: CheckboxOption, ValueContainer: CollapsibleValueContainer }}
                  hideSelectedOptions={false}
                />
              </Col>
            </Row>
          </CardBody>
        </Card>

        {/* ── Table ────────────────────────────────────────────────────────────── */}
        <div
          style={{
            background: "#fff",
            borderRadius: "16px",
            boxShadow: "0 4px 24px rgba(0,0,0,0.06)",
            border: "1px solid #e2e8f0",
            overflow: "hidden",
          }}
        >
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "10px",
              padding: "14px 16px 16px",
            }}
          >
            <button
              onClick={() => setShowZeroRows(v => !v)}
              title={
                showZeroRows
                  ? "Hide rows where all margin columns are 0"
                  : "Show rows where all margin columns are 0"
              }
              style={{
                display: "flex",
                alignItems: "center",
                gap: "6px",
                background: showZeroRows ? "#eff6ff" : "#f1f5f9",
                border: `1.5px solid ${showZeroRows ? "#bfdbfe" : "#e2e8f0"}`,
                borderRadius: "10px",
                padding: "5px 12px",
                cursor: "pointer",
                fontSize: "11px",
                fontWeight: 700,
                color: showZeroRows ? "#2563eb" : "#64748b",
                whiteSpace: "nowrap",
              }}
            >
              {showZeroRows ? "Hide Zero Rows" : "Show Zero Rows"}
            </button>
            <SortButton sortOrder={sortOrder} onToggle={handleSortToggle} />
          </div>
          <div style={{ overflow: "auto", maxHeight: "65vh" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
              <thead style={{ position: "sticky", top: 0, zIndex: 1 }}>
                <tr style={{ background: "#f8fafc", borderBottom: "2px solid #e2e8f0" }}>
                  {[
                    ...(productFirst
                      ? [
                          { label: "Product", align: "left" },
                          { label: "Customer", align: "left" },
                        ]
                      : [
                          { label: "Customer", align: "left" },
                          { label: "Product", align: "left" },
                        ]),
                    {
                      label: "Gross Margin %",
                      align: "right",
                      filter: { value: grossPercentFilter, onToggle: sign => toggleSignFilter(grossPercentFilter, setGrossPercentFilter, sign) },
                    },
                    {
                      label: "Gross Margin Contribution",
                      align: "right",
                      filter: { value: grossSignFilter, onToggle: sign => toggleSignFilter(grossSignFilter, setGrossSignFilter, sign) },
                    },
                    {
                      label: "Net Margin %",
                      align: "right",
                      filter: { value: netPercentFilter, onToggle: sign => toggleSignFilter(netPercentFilter, setNetPercentFilter, sign) },
                    },
                    {
                      label: "Net Margin Contribution",
                      align: "right",
                      filter: { value: netSignFilter, onToggle: sign => toggleSignFilter(netSignFilter, setNetSignFilter, sign) },
                    },
                  ].map(h => (
                    <th
                      key={h.label}
                      style={{
                        padding: "10px 16px",
                        textAlign: h.align,
                        fontSize: "12px",
                        fontWeight: 700,
                        color: "#64748b",
                        whiteSpace: "nowrap",
                        background: "#f8fafc",
                      }}
                    >
                      {h.filter ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "6px" }}>
                          <span>{h.label}</span>
                          <span style={{ display: "inline-flex", flexDirection: "column", gap: "2px" }}>
                            <SignFilterButton sign="positive" value={h.filter.value} onToggle={h.filter.onToggle} />
                            <SignFilterButton sign="negative" value={h.filter.value} onToggle={h.filter.onToggle} />
                          </span>
                        </span>
                      ) : (
                        h.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredTableRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ textAlign: "center", padding: "48px", color: "#94a3b8" }}>
                      No data available
                    </td>
                  </tr>
                ) : (
                  filteredTableRows.map((row, i) => {
                    const netColor = row.netMarginContribution >= 0 ? "#15803d" : "#be123c"
                    const grossColor = row.grossMarginContribution >= 0 ? "#15803d" : "#be123c"
                    return (
                      <tr
                        key={i}
                        style={{
                          borderBottom: "1px solid #f1f5f9",
                          background: i % 2 === 0 ? "#fff" : "#fafbfc",
                        }}
                      >
                        {productFirst ? (
                          <>
                            <td style={{ padding: "10px 16px", color: "#1e293b", fontWeight: 600 }}>
                              {row.productName}
                            </td>
                            <td style={{ padding: "10px 16px", color: "#374151", fontWeight: 500 }}>
                              {row.customerName}
                            </td>
                          </>
                        ) : (
                          <>
                            <td style={{ padding: "10px 16px", color: "#1e293b", fontWeight: 600 }}>
                              {row.customerName}
                            </td>
                            <td style={{ padding: "10px 16px", color: "#374151", fontWeight: 500 }}>
                              {row.productName}
                            </td>
                          </>
                        )}
                        <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: grossColor }}>
                          {row.grossMarginPercent.toFixed(1)}%
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: grossColor }}>
                          {fmtGBP(row.grossMarginContribution)}
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: netColor }}>
                          {row.netMarginPercent.toFixed(1)}%
                        </td>
                        <td style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, color: netColor }}>
                          {fmtGBP(row.netMarginContribution)}
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
              {filteredTableRows.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: "2px solid #e2e8f0", background: "#f8fafc" }}>
                    <td colSpan={3} style={{ padding: "10px 16px", textAlign: "right", fontWeight: 700, fontSize: "13px", color: "#374151" }}>
                      Total
                    </td>
                    <td
                      style={{
                        padding: "10px 16px",
                        textAlign: "right",
                        fontWeight: 800,
                        fontSize: "14px",
                        color: grossTotal >= 0 ? "#15803d" : "#be123c",
                      }}
                    >
                      {fmtGBP(grossTotal)}
                    </td>
                    <td style={{ padding: "10px 16px" }} />
                    <td
                      style={{
                        padding: "10px 16px",
                        textAlign: "right",
                        fontWeight: 800,
                        fontSize: "14px",
                        color: netTotal >= 0 ? "#15803d" : "#be123c",
                      }}
                    >
                      {fmtGBP(netTotal)}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        </div>
      </div>
    </div>
  )
}

export default NetGrossMarginReport
